<template>
  <div class="v2-store-cart">
    <!-- Header row -->
    <div class="v2-store-cart__header">
      <div
        class="v2-store-cart__name"
        @click="onClickCartToggle"
      >
        {{ cart.storeName }}
      </div>
      <div class="v2-store-cart__badges">
        <span class="v2-badge v2-badge-muted">
          Total units: {{ formatNumber(cartValues.totalUnits) || 0 }}
        </span>
        <span class="v2-badge v2-badge-muted">
          Total cost: {{ formatCurrency(Number(cartValues.totalValues), cartValues.currency) }}
        </span>
        <span class="v2-badge v2-badge-muted">
          Total RRP: {{ formatCurrency(Number(cartValues.totalRRP), cartValues.currency) }}
        </span>
        <!-- Chevron toggle -->
        <button
          class="v2-store-cart__chevron"
          @click="onClickCartToggle"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline
              v-if="cartVisible"
              points="18 15 12 9 6 15"
            />
            <polyline
              v-else
              points="6 9 12 15 18 9"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Collapsible body -->
    <div
      v-if="cartVisible"
      class="v2-store-cart__body"
    >
      <!-- Address section -->
      <v2-address-section :cart="cart" />

      <!-- Cart products -->
      <div class="v2-store-cart__products">
        <v2-cart-products
          :cart-id="cart._id"
          :cart-entity-id="cart.entityId"
          :store-name="cart.storeName"
          :cart-products="cart.products"
          :is-multiple-cart="true"
          :external-search-text="externalSearchText"
          :collection-end-date="cart.collectionEndDate"
          @on-product-remove="({action, ...rest}) => trackRemoveProduct(action, cart, { ...rest })"
          @on-change-product-count="(productId) => trackProductCountChange(cart, productId)"
          @on-cart-error="({action, error}) => trackCartErrorEvents(action, cart, error)"
          @update-total="updateCartTotal"
          @update-total-units="updateCartTotalUnits"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { formatNumber } from '@/@core/utils/utils'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { mapState } from 'vuex'
import { POP_EXPANDED_CARTS, PUSH_EXPANDED_CARTS } from '@/store/modules/checkout-v2.module'
import { trackCartErrorEvents, trackRemoveProduct } from '@/views/apps/checkout-v2/trackMultipleStoreCartHelpers'
import V2CartProducts from './V2CartProducts.vue'
import V2AddressSection from './V2AddressSection.vue'

const { formatCurrency } = utils

const { CURRENCY_CONVERSIONS } = c

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'V2StoreCart',
  components: {
    V2CartProducts,
    V2AddressSection,
  },
  props: {
    cart: {
      type: Object,
      required: true,
    },
    // Search text owned by V2RetailerCartList. Forwarded straight through
    // to V2CartProducts so its v-show / displayedProducts filter applies
    // without the multi-store parent having to mutate cart.products.
    externalSearchText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      cartVisible: false,
      formatNumber,
      trackCartErrorEvents,
      trackRemoveProduct,
      formatCurrency,
      cartValues: {
        storeId: this.cart._id,
        storeName: this.cart.storeName,
        totalValues: 0,
        totalUnits: 0,
        totalRRP: 0,
        currency: null,
      },
    }
  },
  computed: {
    ...mapState({
      isCartHasErrors: state => state.checkout.isCartHasErrors,
      expandedCarts: state => state.checkout.expandedCarts,
    }),
  },
  watch: {
    expandedCarts: {
      handler(val) {
        if (!this.cartVisible) {
          this.cartVisible = val.includes(this.cart._id)
        }
      },
      immediate: true,
    },
  },
  created() {
    this.cartValues.currency = this.cart?.products[0]?.currency || CURRENCY_CONVERSIONS.USD.SYMBOL
    this.computeInitialCartValues()
  },
  emits: ['on-updated-cart-values'],
  methods: {
    onClickCartToggle() {
      this.cartVisible = !this.cartVisible
      if (this.cartVisible) {
        this.$store.commit(PUSH_EXPANDED_CARTS, this.cart._id)
      } else {
        this.$store.commit(POP_EXPANDED_CARTS, this.cart._id)
      }
      analytics.track(this.cartVisible ? CART_WITH_MULTIPLE_STORES.RETAILER_EXPANDS_CART : CART_WITH_MULTIPLE_STORES.RERAILER_COLLAPSES_CART, { cart: this.cart })
    },
    updateCartTotal({ totalMRP, totalRRP, currency }) {
      this.cartValues.totalValues = Number(totalMRP).toFixed(2)
      this.cartValues.totalRRP = Number(totalRRP).toFixed(2)
      if (currency) this.cartValues.currency = currency
    },
    updateCartTotalUnits(totalUnits) {
      this.cartValues.totalUnits = totalUnits
      this.$emit('on-updated-cart-values', this.cartValues)
    },
    getItemQtySum(product) {
      return (product.items || []).reduce((sum, item) => sum + (Number(item.value) || 0), 0)
    },
    getProductQty(product, itemQty) {
      return product.isCarton ? (product.quantity || 0) : itemQty
    },
    getUnitPrice(product) {
      return product.discountedPrice || product.price || 0
    },
    computeInitialCartValues() {
      const products = this.cart.products || []
      if (!products.length) return

      let totalUnits = 0
      let totalValues = 0
      let totalRRP = 0

      products.forEach(product => {
        const itemQty = this.getItemQtySum(product)
        const qty = this.getProductQty(product, itemQty)
        totalUnits += qty
        totalValues += this.getUnitPrice(product) * qty
        // Match old calculateLineRRP: retailPrice * (quantity || 1) * sum(item.value)
        totalRRP += (product.retailPrice || 0) * (product.quantity || 1) * itemQty
      })

      this.cartValues.totalUnits = totalUnits
      this.cartValues.totalValues = totalValues.toFixed(2)
      this.cartValues.totalRRP = totalRRP.toFixed(2)
      this.$emit('on-updated-cart-values', this.cartValues)
    },
    trackProductCountChange(cart, productId) {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CHANGES_PRODUCT_COUNT, {
        cart, productId,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-store-cart {
  border: 1px solid var(--v2-border);
  border-radius: var(--v2-radius-lg);
  background: var(--v2-card);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-4);
    gap: var(--v2-space-4);
  }

  &__name {
    font-family: var(--v2-font-heading);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--v2-foreground);
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    flex-shrink: 0;
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--v2-muted-foreground);
    cursor: pointer;
    padding: var(--v2-space-1);
    border-radius: var(--v2-radius-sm);
    transition: color 0.15s ease;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__body {
    padding: 0 var(--v2-space-4) var(--v2-space-4);
  }

  &__products {
    margin-top: var(--v2-space-4);
  }
}

.v2-badge {
  display: inline-flex;
  align-items: center;
  padding: var(--v2-space-1) var(--v2-space-2);
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: var(--v2-radius-md);
  white-space: nowrap;
}

.v2-badge-muted {
  background: var(--v2-muted);
  color: var(--v2-foreground);
}
</style>
